'use client';

import { useEffect, useRef } from 'react';

/** Global reference to the live region element for announcements. */
let liveRegionElement: HTMLDivElement | null = null;

/**
 * Returns the shared live region element used by `useAnnounce`.
 * The element is created once and persisted in the DOM.
 */
export function getLiveRegionElement(): HTMLDivElement | null {
  return liveRegionElement;
}

/**
 * Visually hidden aria-live region that receives screen reader announcements.
 * Mount once at the app root level (e.g., in layout.tsx).
 */
export const LiveRegion = () => {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    liveRegionElement = politeRef.current;
    return () => {
      liveRegionElement = null;
    };
  }, []);

  return (
    <>
      <div
        ref={politeRef}
        aria-live="polite"
        aria-atomic="true"
        role="status"
        data-live-region="polite"
        data-cy="live-region-polite"
        className="tw:sr-only"
      />
      <div
        ref={assertiveRef}
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        data-live-region="assertive"
        data-cy="live-region-assertive"
        className="tw:sr-only"
      />
    </>
  );
};
